<template>
  <div>
    <div class="flex gap-size-5 channels relative text-size-6">
      <a
        v-for="channel in channels"
        :key="channel.enum"
        :href="channel.landingPage"
        class="channel flex gap-[0.5em] font-bold items-center text-muted hover:text-muted-hover pb-[0.1em] border-b-4 border-b-transparent"
        :style="activeChannel === channel.enum ? activeToggleStyle : null"
        :class="{
          '!text-primary is-active': channel.enum === activeChannel,
        }"
        @click="onClick($event, channel)"
      >
        <SpriteSymbol :name="channel.icon" class="size-[0.7em]" />
        <span>{{ channel.label }}</span>
      </a>
    </div>

    <LanguageSwitcherPopup
      v-if="popupChannel"
      :key
      :current-language="language"
      :alternatives="popupChannel.translations"
    />
  </div>
</template>

<script setup lang="ts">
import type { Channel } from '#graphql-operations'
import type { AvailableChannel } from '~/composables/useInitData/types'

defineProps<{
  channels: AvailableChannel[]
  activeChannel: Channel
}>()

const language = useCurrentLanguage()
const router = useRouter()
const { trackEvent } = useAnalytics()

const { activeModal } = useModal()

const activeToggleStyle: Record<string, string> = {
  'anchor-name': '--channel-anchor',
}

const popupChannel = ref<AvailableChannel | null>(null)
const key = ref(0)

function onClick(e: MouseEvent, channel: AvailableChannel) {
  trackEvent({
    event: 'click_context_channel',
    contextChannelName: channel.enum,
  })
  key.value++
  popupChannel.value = null
  e.preventDefault()
  trackEvent({
    event: 'click_context_channel',
    contextChannelName: channel.enum,
  })
  const hasTranslation = !!channel.translations[language.value]
  if (hasTranslation) {
    router.push(channel.landingPage)
    return
  }

  popupChannel.value = channel
}

onMounted(() => {
  if (!popupChannel.value && activeModal.value === 'language-popup') {
    activeModal.value = ''
  }
})
</script>

<style lang="postcss">
.channels {
  a {
    @apply pb-0;
    line-height: 16px;
  }
  /* Fallback when anchor positioning is not supported. */
  a.is-active {
    @apply border-b-primary border-b-4;
  }
}

@supports (position-anchor: inherit) {
  .channels {
    /* Animated underline on the active channel. */
    &:before {
      content: '';
      @apply absolute bottom-0 left-0 bg-primary pointer-events-none h-[3px];
      @apply transition-all duration-300 ease-bounce;
      position-anchor: --channel-anchor;
      left: anchor(left);
      width: anchor-size(width);
    }

    /* Undo the fallback. */
    a.is-active {
      @apply !border-b-transparent;
    }
  }
}
</style>
